import TextCell from "../components/TextCell";
import { Paper, TableBody, TableContainer, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material";
import { useState} from "react";
import SwitchCell from "../components/SwitchCell";
import { ISliderInterface } from "../components/SliderCell";
import { ISelectValue } from "../components/SelectCell";
import { LivenessMode } from "../../../types/FaceVerification";
import ISOLivenessTable from "../components/ISOLivenessTable";
import { DefaultButton, SettingTable, TableCellButton, settingUseStyles } from "../Settings";
import SliderRow from "../components/SliderRow";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";
import { useSnackbar } from "notistack";
import LivenessActionSequenceRow from "../components/LivenessActionSequenceRow";
import LivenessModeSelectRow from "../components/LivenessModeSelectRow";
import SelectCheckmarksRow from "../components/SelectCheckmarksRow";
import SelectRow from "../components/SelectRow";

enum ImageLivenessInputType {
    Type0 = 1,
    Type1 = 2,
    Type2 = 4,
    Type3 = 8,
    Type4 = 16,
    Type5 = 32,
    Type6 = 64,
    Type7 = 128,
    Type8 = 256,
    Type9 = 512,
    Type10 = 1024,
    Type11 = 2048,
    Type12 = 4096,
    Type13 = 8192,
    Type14 = 16384,
    Type15 = 32768,
    Type16 = 65536,
    Type17 = 131072,
    Type18 = 262144,
    Type19 = 524288,
    Type20 = 1048576,
    Type21 = 2097152,
}

interface ILiveness {
    liveness: string,
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    setOption: (key: string, value: string | number | boolean, advanced?: boolean) => void
    findFVStringSetting: (name: string) => string
    handleQualityImageSetting: (w: number, h: number) => void,
    resetValueToDefault: (name: string) => void,
    livenessCustomActionSequence: string
}

const Liveness: React.FC<ILiveness> = (props) => {
    
    const [selectLiveness, setSelectLiveness] = useState(Number(props.liveness) > 0? props.liveness : "1")
    const [active, setActive] = useState<boolean>(Number(props.liveness) > 0? true : false);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const classes = settingUseStyles();
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar();

    const setLiveness = (liveness: boolean) => {
        setActive(liveness)
        if (liveness) {
            props.setOption("livenessMode", Number(selectLiveness), true)
        }
        else
        props.setOption("livenessMode", LivenessMode.NONE, true)
    }
    
    const getEnumValuesAndKey = (): ISelectValue[] => {
        return Object.keys(LivenessMode).map((e) => {
            if (Number(e) > 0) {
                return {key: LivenessMode[e as any], value: e}
            }
            return undefined
        }).filter((el) => el !== undefined) as ISelectValue[]
    }

    const getEnumImageLivenessInputType = (): string[] => {
        return Object.keys(ImageLivenessInputType).map((e) => {
            if (Number(e) > 0) {
                return undefined
            }
            return e
        }).filter((el) => el !== undefined) as string[]
    }

    const getImageLivenessInputType = (): string[] => {
        const type = props.findFVSliderInterface("ImageLivenessInputType")
        if (type?.value) {
            const result: string[] = []
            for (const value of Object.values(ImageLivenessInputType)) {
                if (typeof value === "number" && (type.value & value) === value) {
                    result.push(ImageLivenessInputType[value]);
                }
            }
            return result
        } else {
            return []
        }
    }

    const getDefaultImageLivenessInputType = (): string[] => {
        const type = props.findDefaultFVSliderInterface("ImageLivenessInputType")
        if (type?.value) {
            const result: string[] = []
            for (const value of Object.values(ImageLivenessInputType)) {
                if (typeof value === "number" && (type.value & value) === value) {
                    result.push(ImageLivenessInputType[value]);
                }
            }
            return result
        } else {
            return []
        }
    }

    const setImageLivenessInputType = (types: (keyof typeof ImageLivenessInputType)[]) => {
        let result = 0;

        for (const type of types) {
            result |= ImageLivenessInputType[type];
        }

        props.setOption("ImageLivenessInputType", result)
    };

    const setImageLivenessOutputType = (string: string) => {
        props.setOption("ImageLivenessOutputType", string)
    }

    const handleImageLivenessOutputTypeEnum = (): ISelectValue[] => {
        return [
            {key: "Minimal", value: "Min"},
            {key: "Maximal", value: "Max"},
            {key: "Average", value: "Avg"},
            {key: "Weighted", value: "Weighted"},
        ]
    }

    const handleResetClick = () => {
        enqueueSnackbar("Liveness settings reset", {variant: "info"})
        props.resetValueToDefault("LivenessThreshold")
        props.resetValueToDefault("LivenessBlinkTimeout")
        props.resetValueToDefault("ImageLivenessInputType")
        props.resetValueToDefault("ImageLivenessOutputType")
        props.resetValueToDefault("PassiveLivenessRequiredDetectionCount")
        handleSetLivenessMode("1")
    }

    const handleSetLivenessMode = (mode: string) => {
        setSelectLiveness(mode)
        props.setOption("livenessMode", Number(mode), true)
    }

    const handleLivenessActionSequenceApply = (string: string) => {
        props.setOption("LivenessCustomActionSequence", string)
    }

    return(
        <>
            <TableContainer component={Paper} className={classes.container}>
                <SettingTable>
                    <TableHead>
                        <TableRow>
                            <TextCell head title="Liveness" subtitle=""/>
                            <TableCellButton align="right" colSpan={isMobile? 1 : 2}>
                                <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                    Reset to default
                                </DefaultButton>
                            </TableCellButton>
                            <SwitchCell state={active} setState={setLiveness}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <LivenessModeSelectRow
                            isMobile={isMobile}
                            active={active}
                            getEnumValuesAndKey={getEnumValuesAndKey}
                            selectLiveness={selectLiveness}
                            handleSetLivenessMode={handleSetLivenessMode}
                        />
                        <LivenessActionSequenceRow
                            isMobile={isMobile}
                            active={active && selectLiveness === "5"}
                            options={props.livenessCustomActionSequence}
                            apply={handleLivenessActionSequenceApply}
                        />
                        <SliderRow
                            mobile={isMobile}
                            textTitle="Liveness threshold"
                            subtitle="Determines if liveness prediction is confident enough."
                            title="Threshold range"
                            option={props.findFVSliderInterface("LivenessThreshold")}
                            defaultOption={props.findDefaultFVSliderInterface("LivenessThreshold")}
                            setOption={props.setFVSliderInterfaceOption}
                            minValue={0}
                            maxValue={100}
                            type="raising"
                            active={active}
                            lockMaxValue
                            noWarningMsg="Everything will pass"
                            alwaysWarningMsg="Nothing will pass"
                        />
                        <SliderRow
                            mobile={isMobile}
                            textTitle="Liveness blink timeout"
                            subtitle="Maximum time in ms when user has to blink when asked."
                            title="Threshold range"
                            option={props.findFVSliderInterface("LivenessBlinkTimeout")}
                            defaultOption={props.findDefaultFVSliderInterface("LivenessBlinkTimeout")}
                            setOption={props.setFVSliderInterfaceOption}
                            minValue={0}
                            maxValue={60000}
                            type="static"
                            active={active}
                            lockMaxValue
                            steps={1000}
                        />
                        <SelectCheckmarksRow
                            textTitle="Image Liveness input type"
                            title="Type"
                            subtitle="Specifies which passive liveness model types are applied during image-based liveness evaluation."
                            value={getImageLivenessInputType()}
                            setValue={(value) => setImageLivenessInputType(value)}
                            type={getEnumImageLivenessInputType()}
                            source="STRING"
                            mobile={isMobile}
                            active={active}
                            moreInfo={
                                <>
                                    <strong>ImageLivenessInputType</strong> specifies which passive liveness model types are applied during image-based liveness evaluation.
                                        This is an advanced configuration parameter and should be modified only in consultation with <strong>Neurotechnology support</strong>. The available model types are carefully tuned and designed to operate both independently and in combination, with each model serving a specific role within the overall liveness evaluation strategy.
                                        Model Behavior and Specialization
                                        Different model types are optimized for:
                                        <ul>
                                            <li>
                                                Specific spoofing attack categories
                                            </li>
                                            <li>
                                                Distinct capture environments
                                            </li>
                                            <li>
                                                Varying hardware performance characteristics
                                            </li>
                                            <li>
                                                Particular use-case scenarios
                                            </li>
                                        </ul>
                                        Because of these differences, selecting an inappropriate configuration may negatively impact performance or security.
                                        Mobile-Optimized Variants
                                        Certain input types are specifically adapted for mobile use cases, where:
                                        <ul>
                                            <li>
                                                Computational resources are limited
                                            </li>
                                            <li>
                                                Lighting conditions are less controlled
                                            </li>
                                            <li>
                                                Camera quality may vary significantly
                                            </li>
                                        </ul>
                                        These models prioritize efficiency while maintaining reliable spoof detection performance under mobile constraints.
                                </>
                            }
                        />
                        <SelectRow
                            title="Image Liveness output type"
                            subtitle="Determines how the liveness output is calculated."
                            label="Type"
                            colSpan={isMobile ? 1 : 2}
                            value={props.findFVStringSetting("ImageLivenessOutputType")}
                            isMobile={isMobile}
                            setValue={setImageLivenessOutputType}
                            getEnumValuesAndKey={handleImageLivenessOutputTypeEnum}
                            active={active}
                        />
                        <SliderRow
                            mobile={isMobile}
                            textTitle="Passive liveness required detection count"
                            subtitle="Total number of successful passive liveness detections required for the session to be considered a liveness pass."
                            title="Count"
                            option={props.findFVSliderInterface("PassiveLivenessRequiredDetectionCount")}
                            defaultOption={props.findDefaultFVSliderInterface("PassiveLivenessRequiredDetectionCount")}
                            setOption={props.setFVSliderInterfaceOption}
                            minValue={0}
                            maxValue={36}
                            type="static"
                            active={active}
                            lockMaxValue
                            moreInfo={
                                <>
                                <strong>PassiveLivenessRequiredDetectionCount</strong> defines the total number of successful passive liveness detections required for the session to be considered a liveness pass.
                                At least one passive liveness model must be enabled in order for this parameter to take effect. The same model may be executed multiple times on distinct frames within the stream until the required number of successful detections is reached.
                                <br/>
                                Operational Logic:
                                <ul>
                                    <li>
                                        Each frame that satisfies liveness criteria increments the successful detection counter.
                                    </li>
                                    <li>
                                        Processing continues until the configured threshold is met.
                                    </li>
                                    <li>
                                        Once the required count is reached, the liveness check is marked as successful.
                                    </li>
                                </ul>
                                Performance and Accuracy Optimization
                                This parameter enables fine-grained control over the trade-off between:
                                <ul>
                                    <li>
                                        <strong>Speed</strong> - Lower values result in faster decisions.
                                    </li>
                                    <li>
                                        <strong>Robustness and accuracy</strong> - Higher values increase confidence by requiring consistent liveness validation across multiple frames.
                                    </li>
                                </ul>
                                By adjusting this setting, integrators can optimize behavior according to the target platform capabilities (e.g., mobile device vs. high-performance workstation) and security requirements.
                                </>
                            }
                        />
                    </TableBody>
                </SettingTable>
            </TableContainer>
            <ISOLivenessTable
                setOption={props.setOption}
                setLiveness={(liveness) => props.setOption("livenessMode", Number(liveness), true)}
                setQualityImageSize={props.handleQualityImageSetting}
                active={active}
                findDefaultFVSliderInterface={props.findDefaultFVSliderInterface}
                findFVStringSetting={props.findFVStringSetting}
                imageLivenessInputType={getDefaultImageLivenessInputType()}
            />
            <ConfirmationDialog
                open={openDialog}
                setOpen={setOpenDialog}
                title="Reset liveness settings"
                text="Reset liveness settings to default values?"
                cancelText="Cancel"
                confirmText="Yes"
                confirm={handleResetClick}
                confirmColor="error"
            />
        </>
    );
}

export default Liveness;